import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Container,
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Pagination,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from "@mui/material";
import { EmojiEvents, Search, TrendingUp } from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import rankingService from "../../services/rankingService";

const COLLEGES = [
    "National Institute of Medical Sciences & Research",
    "Nims Institute of Engineering & Technology",
    "Nims School of Business",
    "Nims School of Law",
    "Nims Institute of Hotel Management",
    "Nims Dental College & Hospital",
    "Nims Institute of Pharmacy",
    "Nims School of Nursing",
    "Nims College of Physiotherapy & Occupational Therapy",
    "Nims College of Allied & Health Care Sciences (Paramedical)",
    "Nims Institute of Humanities & Liberal Arts",
    "Nims Institute of Public Health and Governance",
    "Nims Institute of Fashion Technology",
    "Nims Institute of Aviation",
    "Nims School of Forensic Sciences",
    "Marik Institute of Computing, AI, Robotics & Cybernetics",
    "Nims Institute of Travel & Tourism Management"
];

export default function RankingPage() {
    const { userProfile } = useAuth();
    const [ranking, setRanking] = useState([]);
    const [userRank, setUserRank] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [collegeFilter, setCollegeFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [tierFilter, setTierFilter] = useState("all");
    const [page, setPage] = useState(1);
    const pageSize = 12;

    useEffect(() => {
        let active = true;
        const fetchRanking = async () => {
            try {
                setLoading(true);
                setError("");
                let data;

                if (collegeFilter) {
                    data = await rankingService.getRankingByCollege(collegeFilter);
                } else {
                    data = await rankingService.getGlobalRanking();
                }

                if (!active) return;
                setRanking(data || []);

                if (userProfile?.uid) {
                    let rank;
                    if (collegeFilter) {
                        rank = await rankingService.getUserRankInCollege(userProfile.uid, collegeFilter);
                    } else {
                        rank = await rankingService.getUserRank(userProfile.uid);
                    }
                    if (!active) return;
                    setUserRank(rank);
                }
            } catch (error) {
                if (!active) return;
                setError(error?.message || "Failed to load leaderboard.");
            } finally {
                if (active) setLoading(false);
            }
        };

        fetchRanking();
        const intervalId = setInterval(fetchRanking, 30000);
        return () => {
            active = false;
            clearInterval(intervalId);
        };
    }, [userProfile?.uid, collegeFilter]);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, collegeFilter, tierFilter]);

    const getRankMedalColor = (rank) => {
        if (rank === 1) return "#FFD700";
        if (rank === 2) return "#C0C0C0";
        if (rank === 3) return "#CD7F32";
        return "transparent";
    };

    const getTier = (rank) => {
        if (rank <= 3) return "elite";
        if (rank <= 10) return "champion";
        return "rising";
    };

    const filteredRanking = useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        return ranking
            .filter((student) => {
                if (!normalizedSearch) return true;
                const fullName = `${student.firstName || ""} ${student.lastName || ""}`.toLowerCase();
                const username = String(student.username || "").toLowerCase();
                const email = String(student.email || "").toLowerCase();
                const college = String(student.collegeName || "").toLowerCase();
                return fullName.includes(normalizedSearch) || username.includes(normalizedSearch) || email.includes(normalizedSearch) || college.includes(normalizedSearch);
            })
            .filter((student) => {
                if (tierFilter === "all") return true;
                return getTier(student.rank) === tierFilter;
            });
    }, [ranking, searchTerm, tierFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredRanking.length / pageSize));
    const paginatedRanking = filteredRanking.slice((page - 1) * pageSize, page * pageSize);
    const topThree = ranking.slice(0, 3);
    const topTenCount = Math.min(10, ranking.length);

    if (loading) {
        return (
            <AdminLayout title="Ranking">
                <Container maxWidth="xl" sx={{ py: 4 }}>
                    <Card variant="outlined">
                        <CardContent sx={{ textAlign: "center", py: 8 }}>
                            <CircularProgress />
                            <Typography color="text.secondary" sx={{ mt: 1 }}>
                                Loading leaderboard...
                            </Typography>
                        </CardContent>
                    </Card>
                </Container>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title="Ranking & Leaderboard">
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Typography variant="h4" sx={{ mb: 0.5, fontWeight: 700, color: "#003366", display: "flex", alignItems: "center", gap: 1 }}>
                    <EmojiEvents sx={{ color: "#C1272D" }} />
                    Student Credit Leaderboard
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Ranked by current credit balance: total earned minus consumed credits
                </Typography>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary">YOUR RANK</Typography>
                                {userRank ? (
                                    <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: "#003366" }}>#{userRank.rank}</Typography>
                                            <Typography variant="caption" color="text.secondary">{collegeFilter ? "In College" : "Global"}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700, color: "#C1272D" }}>{userRank.currentCreditBalance}</Typography>
                                            <Typography variant="caption" color="text.secondary">Credit Balance</Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="h4" sx={{ fontWeight: 700 }}>{Math.round(userRank.percentile)}%</Typography>
                                            <Typography variant="caption" color="text.secondary">Percentile</Typography>
                                        </Box>
                                    </Stack>
                                ) : (
                                    <Alert severity="info" sx={{ mt: 1.5 }}>No ranking data available yet</Alert>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Card variant="outlined" sx={{ height: "100%" }}>
                            <CardContent>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>TOP PERFORMANCE TIERS</Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap">
                                    <Chip size="small" label="Elite (Top 3)" sx={{ bgcolor: "#fff3cd", color: "#8a6d3b" }} />
                                    <Chip size="small" label="Champion (4-10)" sx={{ bgcolor: "#eaf2ff", color: "#003366" }} />
                                    <Chip size="small" label="Rising (11+)" sx={{ bgcolor: "#f3f4f6", color: "#374151" }} />
                                </Stack>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
                                    Rankings auto-refresh every 30 seconds when credit values change.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <EmojiEvents sx={{ color: "#C1272D" }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#003366" }}>
                                Top Performers
                            </Typography>
                            <Chip size="small" label={`Top ${topTenCount}`} sx={{ bgcolor: "#eef5ff", color: "#003366", fontWeight: 700 }} />
                        </Stack>
                        <Grid container spacing={1.5}>
                            {topThree.map((student) => (
                                <Grid item xs={12} md={4} key={student.id}>
                                    <Box
                                        sx={{
                                            p: 1.5,
                                            border: "1px solid #e5e7eb",
                                            borderRadius: 1.5,
                                            backgroundColor: student.rank === 1 ? "#fff8e1" : "white"
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.25} alignItems="center">
                                            <Avatar src={student.avatarUrl || ""} sx={{ bgcolor: "#003366" }}>
                                                {(student.firstName || "S").charAt(0)}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700, color: "#003366" }} noWrap>
                                                    #{student.rank} {student.firstName} {student.lastName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                    Balance: {student.currentCreditBalance}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ mb: 2 }}>
                    <CardContent>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Search Students"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" />
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Filter by College</InputLabel>
                                    <Select
                                        value={collegeFilter}
                                        label="Filter by College"
                                        onChange={(e) => setCollegeFilter(e.target.value)}
                                    >
                                        <MenuItem value="">Global Leaderboard</MenuItem>
                                        {COLLEGES.map((college) => (
                                            <MenuItem key={college} value={college}>{college}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <FormControl size="small" fullWidth>
                                    <InputLabel>Ranking Tier</InputLabel>
                                    <Select
                                        value={tierFilter}
                                        label="Ranking Tier"
                                        onChange={(e) => setTierFilter(e.target.value)}
                                    >
                                        <MenuItem value="all">All Tiers</MenuItem>
                                        <MenuItem value="elite">Elite</MenuItem>
                                        <MenuItem value="champion">Champion</MenuItem>
                                        <MenuItem value="rising">Rising</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {filteredRanking.length === 0 ? (
                    <Alert severity="info">No students match your current filters.</Alert>
                ) : (
                    <TableContainer component={Paper} sx={{ border: "1px solid #e5e7eb" }}>
                        <Table>
                            <TableHead sx={{ backgroundColor: "#eef5ff" }}>
                                <TableRow>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>
                                        Rank
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Student</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Earned</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Consumed</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>Balance</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                                        Tier
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedRanking.map((student) => (
                                    <TableRow
                                        key={student.id}
                                        hover
                                        sx={{
                                            backgroundColor:
                                                student.id === userProfile?.uid
                                                    ? "action.selected"
                                                    : student.rank <= 3
                                                        ? "#fffaf0"
                                                        : "inherit"
                                        }}
                                    >
                                        <TableCell align="center">
                                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                {student.rank <= 3 ? (
                                                    <EmojiEvents sx={{ color: getRankMedalColor(student.rank) }} />
                                                ) : (
                                                    <Typography sx={{ fontWeight: 700 }}>{student.rank}</Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Avatar src={student.avatarUrl || ""} sx={{ width: 36, height: 36, backgroundColor: "#003366" }}>
                                                    {(student.firstName || student.username || "S").charAt(0)}
                                                </Avatar>
                                                <Box>
                                                    <Typography sx={{ fontWeight: 600 }}>
                                                        {student.firstName} {student.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {student.collegeName || "College not set"}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography sx={{ fontWeight: 600, color: "#003366" }}>
                                                {student.totalCreditsEarned}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography sx={{ fontWeight: 600, color: "#C1272D" }}>
                                                {student.totalCreditsConsumed}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                                                <TrendingUp sx={{ fontSize: 16, color: "#003366" }} />
                                                <Typography sx={{ fontWeight: 700 }}>{student.currentCreditBalance}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                size="small"
                                                label={getTier(student.rank).toUpperCase()}
                                                sx={{
                                                    bgcolor:
                                                        getTier(student.rank) === "elite"
                                                            ? "#fff3cd"
                                                            : getTier(student.rank) === "champion"
                                                                ? "#eaf2ff"
                                                                : "#f3f4f6",
                                                    color:
                                                        getTier(student.rank) === "elite"
                                                            ? "#8a6d3b"
                                                            : getTier(student.rank) === "champion"
                                                                ? "#003366"
                                                                : "#374151",
                                                    fontWeight: 700
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {filteredRanking.length > pageSize && (
                    <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                        <Pagination
                            count={totalPages}
                            page={page}
                            onChange={(_, newPage) => setPage(newPage)}
                            color="primary"
                            shape="rounded"
                        />
                    </Stack>
                )}
            </Container>
        </AdminLayout>
    );
}
